import { MenuItemsType } from "@/layouts/carbon/carbon-menu-items";
import { EFeatureType } from "@/utilities/types/AdvanceFeature";
import { Roles } from "@/utilities/types/Users";
import cn from "@/utils/class-names";
import { PiCaretDownBold } from "react-icons/pi";
import { Link } from "react-router-dom";
import { MenuLink } from "./MenuLink";
import { hasAdvanceFeatureAccess } from "./PermissionUtils";

interface UserFeature {
  enabled: boolean;
  feature: {
    enabled: boolean;
    type: EFeatureType;
  };
}

interface SecondLevelMenuItemProps {
  parentId: string;
  item: MenuItemsType;
  pathname: string;
  expandedMenus: string[];
  toggleMenu: (menuId: string) => void;
  closeDrawer: () => void;
  currentRole: Roles;
  userFeatures?: UserFeature[];
}

export function SecondLevelMenuItem({
  parentId,
  item,
  pathname,
  expandedMenus,
  toggleMenu,
  closeDrawer,
  currentRole,
  userFeatures,
}: SecondLevelMenuItemProps) {
  const Icon = item.icon;
  const isChildActive = pathname === (item.href as string);

  // Unique ID for this menu level
  const menuId = `${parentId}-${item.name}`;

  // Check if this menu has submenu items
  const hasSubMenu = item.subMenuItems && item.subMenuItems.length > 0;

  // Check if any submenu item is active
  const hasActiveChild =
    item.subMenuItems?.some((subItem) => subItem.href === pathname) || false;

  // Dropdown open/close state, completely independent of active state
  const isDropdownOpen = expandedMenus.includes(menuId);

  // Active when this path is selected or when a submenu is selected
  const isActive = isChildActive || hasActiveChild;

  // If this is a collapsible submenu
  if (hasSubMenu) {
    return (
      <div className="w-full">
        {/* Second level menu header */}
        <button
          onClick={() => toggleMenu(menuId)}
          className={cn(
            "group relative flex w-full cursor-pointer items-center justify-between rounded-md px-3.5 py-2 font-medium",
            isActive
              ? "before:top-2/5 rounded-md bg-gray-100 text-primary before:absolute before:start-0 before:block before:h-4/5 before:w-1 before:rounded-ee-md before:rounded-se-md before:bg-primary dark:bg-gray-200 2xl:before:start-0"
              : "text-gray-700 transition-all duration-200 hover:bg-gray-100 dark:text-gray-700/90 hover:dark:bg-gray-200 dark:hover:text-gray-700"
          )}
        >
          <span className="flex items-center">
            {Icon && (
              <span
                className={cn(
                  "me-2 inline-flex h-5 w-5 items-center justify-center rounded-md [&>svg]:h-[20px] [&>svg]:w-[20px]",
                  isActive
                    ? "text-primary"
                    : "text-gray-400 dark:text-gray-500 dark:group-hover:text-gray-700"
                )}
              >
                <Icon />
              </span>
            )}
            {item.name}
          </span>

          <PiCaretDownBold
            strokeWidth={3}
            className={cn(
              "h-3.5 w-3.5 transition-transform duration-200",
              isDropdownOpen ? "rotate-180" : "",
              isActive ? "text-primary" : "text-gray-900"
            )}
          />
        </button>

        {/* Submenu content */}
        <div
          className={cn(
            "overflow-hidden transition-all duration-300",
            isDropdownOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="ml-4 mt-1 space-y-1">
            {item.subMenuItems?.map((subMenuItem, index) => {
              // Check role permission for this submenu item
              const hasRolePermission =
                subMenuItem.permissions?.includes(currentRole);

              // Check advance feature access (if needed)
              // Treat subMenuItems as possibly having MenuItemsType features
              const menuSubItem = subMenuItem;
              const hasFeatureAccess = hasAdvanceFeatureAccess(
                currentRole,
                userFeatures,
                menuSubItem.advanceFeatureType
              );

              // Skip rendering if user doesn't have permission
              if (!(hasRolePermission && hasFeatureAccess)) {
                return null;
              }

              const isSubItemActive =
                pathname === (subMenuItem?.href as string);

              return (
                <Link
                  key={`sub-menu-${subMenuItem.name}-${index}`}
                  to={subMenuItem?.href ?? ""}
                  className={cn(
                    "relative flex w-full items-center justify-between rounded-md px-3.5 py-2 font-medium capitalize text-gray-900",
                    isSubItemActive
                      ? "text-primary"
                      : "text-gray-900 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-200"
                  )}
                  onClick={() => closeDrawer()}
                >
                  <span className="flex items-center truncate">
                    {subMenuItem.icon && (
                      <span
                        className={cn(
                          "me-2 inline-flex h-4 w-4 items-center justify-center rounded-md [&>svg]:h-[16px] [&>svg]:w-[16px]",
                          isSubItemActive
                            ? "text-primary"
                            : "text-gray-400 dark:text-gray-500 dark:group-hover:text-gray-700"
                        )}
                      >
                        <subMenuItem.icon />
                      </span>
                    )}
                    <span className="truncate">{subMenuItem.name}</span>
                  </span>
                  {/* {subMenuItem.badge && subMenuItem.badge.length ? (
                    <StatusBadge status={subMenuItem.badge} />
                  ) : null} */}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // If this is a simple menu link
  return (
    <MenuLink item={item} isChildActive={isChildActive} onClick={closeDrawer} />
  );
}
